import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API, useTheme } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  LayoutDashboard, User, GraduationCap, FileText, DollarSign, CheckCircle2,
  LogOut, Plus, Clock, AlertCircle, ChevronRight, Calendar, Upload,
  MessageCircle, Sparkles, X, Phone, Mail, Video, CalendarDays, MapPin, BookOpen,
  Sun, Moon, Star, Pencil, Lock, Send
} from "lucide-react";
import AIChat from "@/components/AIChat";
import { EmailModal, TextModal, CallModal, ScheduleModal } from "@/components/AdvisorContactModals";
import VerificationModal from "@/components/VerificationModal";

// Program pathways data
const PROGRAM_PATHWAYS = {
  ot: {
    name: "Occupational Therapy",
    color: "#00677F",
    programs: [
      { id: "mot", name: "Master of Occupational Therapy" },
      { id: "mot_ota", name: "Master of Occupational Therapy (OTA Entry)" },
      { id: "flex_mot", name: "Flex Master of Occupational Therapy" },
      { id: "flex_mot_ota", name: "Flex Master of Occupational Therapy (OTA Entry)" },
      { id: "hybrid_mot", name: "Hybrid Immersion Master of Occupational Therapy" },
      { id: "hybrid_mot_ota", name: "Hybrid Immersion Master of Occupational Therapy (OTA Entry)" },
      { id: "otd", name: "Doctor of Occupational Therapy" },
      { id: "flex_otd", name: "Flex Doctor of Occupational Therapy" },
      { id: "hybrid_otd", name: "Hybrid Immersion Doctor of Occupational Therapy" },
      { id: "ppotd", name: "Post Professional Doctor of Occupational Therapy" },
    ]
  },
  nursing: {
    name: "Nursing",
    color: "#7B68EE",
    programs: [
      { id: "msn_fnp", name: "MSN - Family Nurse Practitioner" },
      { id: "msn_ne", name: "MSN - Nurse Executive" },
      { id: "msn_pmhnp", name: "MSN - Psychiatric Mental Health Nurse Practitioner" },
      { id: "rn_msn_fnp", name: "RN to MSN - Family Nurse Practitioner" },
      { id: "rn_msn_pmhnp", name: "RN to MSN - Psychiatric Mental Health Nurse Practitioner" },
      { id: "bsn_dnp_ne", name: "BSN to DNP - Nurse Executive" },
      { id: "bsn_dnp_fnp", name: "BSN to DNP - Family Nurse Practitioner" },
      { id: "bsn_dnp_pmhnp", name: "BSN to DNP - Psychiatric Mental Health Nurse Practitioner" },
      { id: "msn_dnp", name: "MSN to DNP" },
    ]
  },
  education: {
    name: "Education",
    color: "#FF9800",
    programs: [
      { id: "edd", name: "Doctor of Education" },
      { id: "edd_el", name: "EdD - Executive Leadership" },
      { id: "edd_tl", name: "EdD - Teaching and Learning" },
      { id: "edd_ne", name: "EdD - Nursing Education" },
      { id: "edd_at", name: "EdD - Athletic Training" },
    ]
  },
  certificates: {
    name: "Certificates",
    color: "#739600",
    programs: [
      { id: "cert_fnp", name: "Certificate - Family Nurse Practitioner" },
      { id: "cert_ne", name: "Certificate - Nursing Executive" },
      { id: "cert_bi", name: "Graduate Certificate in Business Intelligence" },
      { id: "cert_el", name: "Graduate Certificate in Executive Leadership" },
      { id: "cert_ipe", name: "Graduate Certificate in Interprofessional Education (IPE)" },
    ]
  }
};

const START_TERMS = [
  { id: "summer_2026", name: "Summer 2026 (May 11)" },
  { id: "fall_2026", name: "Fall 2026 (September 8)" },
  { id: "spring_2027", name: "Spring 2027 (January 11)" },
];

const CAMPUSES = [
  { id: "miami", name: "Miami, FL" },
  { id: "st_augustine", name: "St. Augustine, FL" },
  { id: "austin", name: "Austin, TX" },
  { id: "dallas", name: "Dallas, TX" },
  { id: "san_marcos", name: "San Marcos, CA" },
  { id: "distance", name: "Distance" },
];

// Only OT programs get campus selection; all others default to Distance Campus
const isOTProgram = (category) => category === "ot";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewAppModal, setShowNewAppModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedStartTerm, setSelectedStartTerm] = useState(null);
  const [selectedCampus, setSelectedCampus] = useState(null);
  const [selectedCampus2, setSelectedCampus2] = useState(null);
  const [creatingApp, setCreatingApp] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [advisor, setAdvisor] = useState(null);
  const [modalStep, setModalStep] = useState(1);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [editingAppId, setEditingAppId] = useState(null); // Track which app is being edited

  useEffect(() => {
    fetchApplications();
    fetchAdvisor();
    
    // Check if user email is not verified
    if (user && !user.email_verified) {
      setShowVerificationModal(true);
    }
    
    if (location.state?.newUser) {
      toast.success(`Welcome, ${user?.first_name}! Let's start your application.`);
      if (!user?.email_verified) {
        // Don't show new app modal until verified
      } else {
        setShowNewAppModal(true);
      }
      if (location.state?.program) {
        setSelectedCategory(location.state.program);
      }
    }
  }, []);

  const handleVerificationComplete = () => {
    setShowVerificationModal(false);
    updateUser({ email_verified: true });
    toast.success("Email verified! You can now start your application.");
    setShowNewAppModal(true);
  };

  const fetchAdvisor = async () => {
    try {
      const response = await axios.get(`${API}/auth/advisor`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdvisor(response.data);
    } catch (error) {
      console.error("Error fetching advisor:", error);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const createApplication = async () => {
    const isOT = isOTProgram(selectedCategory);
    const effectiveCampus = isOT ? selectedCampus : "distance";
    const effectiveCampus2 = isOT ? selectedCampus2 : null;

    if (!selectedProgram || !selectedStartTerm || (isOT && !selectedCampus)) {
      toast.error("Please complete all required selections");
      return;
    }

    setCreatingApp(true);
    try {
      // Find the program details
      const categoryData = PROGRAM_PATHWAYS[selectedCategory];
      const programData = categoryData?.programs.find(p => p.id === selectedProgram);
      
      const response = await axios.post(
        `${API}/applications`,
        { 
          program_type: categoryData?.name || "Occupational Therapy",
          program_pathway: programData?.name || selectedProgram,
          start_term: START_TERMS.find(t => t.id === selectedStartTerm)?.name || selectedStartTerm,
          primary_campus: CAMPUSES.find(c => c.id === effectiveCampus)?.name || effectiveCampus,
          secondary_campus: effectiveCampus2 ? CAMPUSES.find(c => c.id === effectiveCampus2)?.name : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Application created successfully!");
      setShowNewAppModal(false);
      resetModalState();
      navigate(`/application/${response.data.id}`);
    } catch (error) {
      console.error("Error creating application:", error);
      toast.error("Failed to create application");
    } finally {
      setCreatingApp(false);
    }
  };

  const resetModalState = () => {
    setSelectedCategory(null);
    setSelectedProgram(null);
    setSelectedStartTerm(null);
    setSelectedCampus(null);
    setSelectedCampus2(null);
    setModalStep(1);
    setEditingAppId(null);
  };

  const closeModal = () => {
    setShowNewAppModal(false);
    resetModalState();
  };

  // Open edit modal pre-filled with current application data
  const openEditModal = (app) => {
    if (app.status === "submitted") return;
    
    const ps = app.program_selection || {};
    
    // Find the category key from program_type
    const catKey = Object.keys(PROGRAM_PATHWAYS).find(
      k => PROGRAM_PATHWAYS[k].name === ps.program_type
    );
    if (catKey) setSelectedCategory(catKey);
    
    // Find the program id from pathway name
    if (catKey) {
      const prog = PROGRAM_PATHWAYS[catKey].programs.find(p => p.name === ps.program_pathway);
      if (prog) setSelectedProgram(prog.id);
    }
    
    // Find start term id
    const term = START_TERMS.find(t => t.name === ps.start_term);
    if (term) setSelectedStartTerm(term.id);
    
    // Find campus ids
    const campus1 = CAMPUSES.find(c => c.name === (ps.campus || ps.primary_campus));
    if (campus1) setSelectedCampus(campus1.id);
    
    const campus2 = CAMPUSES.find(c => c.name === ps.secondary_campus);
    if (campus2) setSelectedCampus2(campus2.id);
    
    setEditingAppId(app.id);
    setModalStep(1);
    setShowNewAppModal(true);
  };

  // Update existing application's program selection
  const updateProgramSelection = async () => {
    const isOT = isOTProgram(selectedCategory);
    const effectiveCampus = isOT ? selectedCampus : "distance";
    const effectiveCampus2 = isOT ? selectedCampus2 : null;

    if (!selectedProgram || !selectedStartTerm || (isOT && !selectedCampus)) {
      toast.error("Please complete all required selections");
      return;
    }

    setCreatingApp(true);
    try {
      const categoryData = PROGRAM_PATHWAYS[selectedCategory];
      const programData = categoryData?.programs.find(p => p.id === selectedProgram);
      
      await axios.put(
        `${API}/applications/${editingAppId}`,
        { 
          program_selection: {
            program_type: categoryData?.name || "Occupational Therapy",
            program_pathway: programData?.name || selectedProgram,
            start_term: START_TERMS.find(t => t.id === selectedStartTerm)?.name || selectedStartTerm,
            campus: CAMPUSES.find(c => c.id === effectiveCampus)?.name || effectiveCampus,
            secondary_campus: effectiveCampus2 ? CAMPUSES.find(c => c.id === effectiveCampus2)?.name : null
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Program selection updated successfully!");
      setShowNewAppModal(false);
      resetModalState();
      fetchApplications();
    } catch (error) {
      console.error("Error updating program:", error);
      toast.error(error.response?.data?.detail || "Failed to update program selection");
    } finally {
      setCreatingApp(false);
    }
  };

  const activeApp = applications.find(a => a.status !== "submitted");
  const submittedApps = applications.filter(a => a.status === "submitted");
  
  // For stats display: use active app if exists, otherwise most recent submitted app
  const displayApp = activeApp || (submittedApps.length > 0 ? submittedApps[0] : null);

  const getDeadline = () => {
    const now = new Date();
    const deadlines = [
      { term: "Spring 2026", date: new Date("2025-11-15") },
      { term: "Summer 2026", date: new Date("2026-02-15") },
      { term: "Fall 2026", date: new Date("2026-05-15") }
    ];
    return deadlines.find(d => d.date > now) || deadlines[deadlines.length - 1];
  };

  const deadline = getDeadline();
  const daysUntilDeadline = Math.ceil((deadline.date - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#0A0E14]' : 'bg-gray-50'}`}>
      {/* Email Verification Modal */}
      <VerificationModal
        isOpen={showVerificationModal}
        email={user?.email}
        firstName={user?.first_name}
        verificationToken={user?.verification_token}
        onVerified={handleVerificationComplete}
        canClose={false}
      />

      {/* Top Navigation */}
      <nav className={`sticky top-0 z-40 backdrop-blur-xl border-b ${theme === 'dark' ? 'bg-[#0A0E14]/80 border-white/5' : 'bg-white/80 border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src={theme === 'dark' 
                ? "https://customer-assets.emergentagent.com/job_be4bb2aa-ca20-4b1a-9d54-431ad1ac86d0/artifacts/ie0vbsv3_primary.Horiz.2line.knockedout.whouter.pms315%2Bwhite.png"
                : "https://customer-assets.emergentagent.com/job_student-journey-17/artifacts/lcfdritp_primary.Horiz.2line.knockedout.pms315.png"
              }
              alt="University of St. Augustine for Health Sciences" 
              className="h-10 sm:h-14 w-auto"
            />
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Theme Toggle Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={`rounded-full ${theme === 'dark' ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            
            {/* Ask Journey Button */}
            <Button
              onClick={() => setShowChat(true)}
              className={`bg-gradient-to-r from-[#00677F]/20 to-[#7B68EE]/20 border border-[#00677F]/30 hover:border-[#A1D8E0] hover:bg-[#00677F]/30 rounded-full px-4 py-2 flex items-center gap-2 transition-all ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}
              data-testid="ask-journey-btn"
            >
              <Sparkles className="w-4 h-4 text-[#A1D8E0]" />
              <span className="hidden sm:inline">Ask Journey</span>
            </Button>
            
            <div className="text-right hidden sm:block">
              <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user?.first_name} {user?.last_name}</p>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>{user?.email}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00677F] to-[#7B68EE] flex items-center justify-center">
              <span className="text-white font-bold">{user?.first_name?.[0]}</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className={`${theme === 'dark' ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h1 className={`font-['Montserrat'] text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Welcome back, {user?.first_name}!
          </h1>
          <p className={theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}>
            {activeApp ? "Continue your application journey" : "Start your application to USA.edu"}
          </p>
        </div>

        {/* Application Review Status Progress Bar - Shows for submitted applications */}
        {submittedApps.length > 0 && (
          <div className="glass-card rounded-2xl p-6 mb-8" data-testid="review-status-progress">
            <h3 className={`font-['Montserrat'] text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Admission Status
            </h3>
            {(() => {
              const latestSubmitted = submittedApps[0];
              const reviewStatus = latestSubmitted?.review_status || "application_received";
              const statuses = [
                { id: "application_received", label: "Application Received", color: "#00677F" },
                { id: "under_review", label: "Under Review", color: "#FF9800" },
                { id: "decision", label: "Decision", color: "#7B68EE" },
                { id: "admitted", label: "Admitted", color: "#739600" }
              ];
              
              // Determine current step index
              let currentIndex = statuses.findIndex(s => s.id === reviewStatus);
              if (currentIndex === -1) currentIndex = 0;
              const isDecision = reviewStatus === "decision";
              const isAdmitted = reviewStatus === "admitted";
              
              return (
                <div className="relative">
                  {/* Progress Line Background */}
                  <div 
                    className="absolute top-5 left-0 right-0 h-1 rounded-full mx-8"
                    style={{ backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}
                  />
                  {/* Progress Line Active */}
                  <div 
                    className="absolute top-5 left-0 h-1 rounded-full mx-8 transition-all duration-500"
                    style={{ 
                      width: `calc(${(currentIndex / (statuses.length - 1)) * 100}% - 64px)`,
                      backgroundColor: isAdmitted ? "#739600" : isDecision ? "#7B68EE" : statuses[currentIndex]?.color || "#00677F"
                    }}
                  />
                  
                  {/* Status Steps */}
                  <div className="relative flex justify-between">
                    {statuses.map((status, index) => {
                      const isActive = status.id === reviewStatus;
                      const isPast = index < currentIndex;
                      
                      return (
                        <div key={status.id} className="flex flex-col items-center" data-testid={`status-step-${status.id}`}>
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all"
                            style={{
                              borderColor: isActive ? status.color : isPast ? "#739600" : (theme === 'dark' ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"),
                              backgroundColor: isActive ? `${status.color}20` : isPast ? "rgba(40,167,69,0.2)" : (theme === 'dark' ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)")
                            }}
                          >
                            {isPast ? (
                              <CheckCircle2 className="w-5 h-5 text-[#739600]" />
                            ) : isActive ? (
                              <div 
                                className="w-3 h-3 rounded-full animate-pulse"
                                style={{ backgroundColor: status.color }}
                              />
                            ) : (
                              <div 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)' }}
                              />
                            )}
                          </div>
                          <span className={`mt-2 text-[10px] sm:text-xs font-medium text-center max-w-[60px] sm:max-w-[80px] ${
                            isActive 
                              ? (theme === 'dark' ? "text-white" : "text-gray-900")
                              : isPast 
                                ? "text-[#739600]" 
                                : (theme === 'dark' ? "text-slate-500" : "text-gray-500")
                          }`}>
                            {status.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Application Progress Card */}
          <div className="glass-card rounded-2xl p-6" data-testid="progress-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>Application Progress</h3>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-[#A1D8E0]/10' : 'bg-[#00677F]/10'}`}>
                <Clock className={`w-5 h-5 ${theme === 'dark' ? 'text-[#A1D8E0]' : 'text-[#00677F]'}`} />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative w-20 h-20">
                <svg className="transform -rotate-90 w-20 h-20">
                  <circle cx="40" cy="40" r="36" fill="none" stroke={theme === 'dark' ? '#1E293B' : '#E2E8F0'} strokeWidth="6" />
                  <circle cx="40" cy="40" r="36" fill="none" stroke={displayApp?.status === "submitted" ? "#739600" : "#00677F"} strokeWidth="6" strokeDasharray={`${(displayApp?.progress || 0) * 2.26} 226`} strokeLinecap="round" className="progress-ring__circle" />
                </svg>
                <span className={`absolute inset-0 flex items-center justify-center text-2xl font-['Montserrat'] font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {displayApp?.progress || 0}%
                </span>
              </div>
              <div>
                <p className={`font-semibold text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {displayApp?.status === "submitted" 
                    ? "Complete" 
                    : displayApp 
                      ? (() => {
                        const OTA_ENTRY_PW = [
                          "Master of Occupational Therapy (OTA Entry)",
                          "Flex Master of Occupational Therapy (OTA Entry)",
                          "Hybrid Immersion Master of Occupational Therapy (OTA Entry)",
                        ];
                        const isOTAE = OTA_ENTRY_PW.includes(displayApp.program_selection?.program_pathway);
                        const RECO = ["Nursing", "Education", "Certificates"];
                        const hasReco = RECO.includes(displayApp.program_selection?.program_type);
                        let total = 5; // Personal, Academic, Documents, Transcripts, Review
                        if (isOTAE) total++;
                        if (hasReco) total++;
                        return `Step ${displayApp.current_step} of ${total}`;
                      })() 
                      : "No Application"}
                </p>
                <p className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>
                  {displayApp?.status === "submitted" ? "Submitted" : displayApp ? "In Progress" : (
                    <button 
                      onClick={() => setShowNewAppModal(true)}
                      className="text-[#00677F] hover:text-[#A1D8E0] underline transition-colors"
                      data-testid="start-application-btn"
                    >
                      Start one now
                    </button>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Deadline Card */}
          <div className="glass-card rounded-2xl p-6" data-testid="deadline-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>Next Deadline</h3>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-[#FF6B35]/10' : 'bg-[#FF6B35]/10'}`}>
                <Calendar className="w-5 h-5 text-[#FF6B35]" />
              </div>
            </div>
            <p className={`text-3xl font-['Montserrat'] font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {daysUntilDeadline} Days
            </p>
            <p className={theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}>{deadline.term} Application</p>
            <p className={`text-sm mt-2 ${theme === 'dark' ? 'text-slate-500' : 'text-gray-400'}`}>
              Due: {deadline.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Documents Card */}
          <div className="glass-card rounded-2xl p-6" data-testid="documents-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>Documents</h3>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-[#739600]/10' : 'bg-[#739600]/10'}`}>
                <FileText className="w-5 h-5 text-[#739600]" />
              </div>
            </div>
            {displayApp ? (
              <>
                <p className={`text-3xl font-['Montserrat'] font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {displayApp.documents?.filter(d => d.type !== "recommendation" && d.status === "uploaded").length || 0}/{displayApp.documents?.filter(d => d.type !== "recommendation").length || 4}
                </p>
                <p className={theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}>Documents Uploaded</p>
                <div className="mt-3">
                  <Progress 
                    value={((displayApp.documents?.filter(d => d.type !== "recommendation" && d.status === "uploaded").length || 0) / (displayApp.documents?.filter(d => d.type !== "recommendation").length || 4)) * 100} 
                    className={`h-2 ${theme === 'dark' ? 'bg-slate-800' : 'bg-gray-200'}`}
                  />
                </div>
              </>
            ) : (
              <>
                <p className={`text-3xl font-['Montserrat'] font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>0/4</p>
                <p className={theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}>Documents Uploaded</p>
              </>
            )}
          </div>
        </div>

        {/* Active Application */}
        {activeApp && (
          <div className="glass-card rounded-2xl p-5 sm:p-8 mb-6 sm:mb-8" data-testid="active-application-card">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {activeApp.status === "submitted" ? (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#739600]/20 text-[#739600] flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Submitted
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#00677F]/20 text-[#00677F]">
                      Active Application
                    </span>
                  )}
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#7B68EE]/20 text-[#7B68EE]">
                    {activeApp.program_selection?.program_type || "Program"}
                  </span>
                </div>
                <h2 className={`font-['Montserrat'] text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {activeApp.program_selection?.program_pathway || activeApp.program_selection?.program_type || "Healthcare"} 
                </h2>
                
                {/* Program Details Grid */}
                <div className="flex items-center gap-2 mt-4 mb-3">
                  <span className={`text-xs uppercase tracking-wider ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>Program Details</span>
                  {activeApp.status !== "submitted" && (
                    <button 
                      onClick={() => openEditModal(activeApp)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-[#00677F] transition-all ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}
                      data-testid="edit-program-btn"
                    >
                      <Pencil className="w-3 h-3" />
                      Edit
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                  {activeApp.program_selection?.program_pathway && (
                    <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-white/5 border border-white/[0.05]' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <GraduationCap className="w-3.5 h-3.5 text-[#7B68EE]" />
                        <span className={`text-xs uppercase tracking-wider ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>Pathway</span>
                      </div>
                      <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{activeApp.program_selection.program_pathway}</p>
                    </div>
                  )}
                  {(activeApp.program_selection?.campus || activeApp.program_selection?.primary_campus) && (
                    <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-white/5 border border-white/[0.05]' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-3.5 h-3.5 text-[#00677F]" />
                        <span className={`text-xs uppercase tracking-wider ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>Primary Campus</span>
                      </div>
                      <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{activeApp.program_selection.campus || activeApp.program_selection.primary_campus}</p>
                    </div>
                  )}
                  {activeApp.program_selection?.start_term && (
                    <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-white/5 border border-white/[0.05]' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-3.5 h-3.5 text-[#FF9800]" />
                        <span className={`text-xs uppercase tracking-wider ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>Start Term</span>
                      </div>
                      <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{activeApp.program_selection.start_term}</p>
                    </div>
                  )}
                  {activeApp.program_selection?.secondary_campus && activeApp.program_selection?.program_type === "Occupational Therapy" && (
                    <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-white/5 border border-white/[0.05]' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-3.5 h-3.5 text-[#739600]" />
                        <span className={`text-xs uppercase tracking-wider ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>Backup Campus</span>
                      </div>
                      <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{activeApp.program_selection.secondary_campus}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <Button
                onClick={() => navigate(`/application/${activeApp.id}`)}
                className={`${activeApp.status === "submitted" 
                  ? "bg-[#739600] hover:bg-[#218838] shadow-[0_0_20px_rgba(40,167,69,0.3)]" 
                  : "bg-[#00677F] hover:bg-[#135163] shadow-[0_0_20px_rgba(0,180,216,0.3)] hover:shadow-[0_0_30px_rgba(0,180,216,0.5)]"
                } text-white rounded-full px-8 py-6 transition-all`}
                data-testid="continue-application-btn"
              >
                {activeApp.status === "submitted" ? (
                  <>
                    <Lock className="mr-2 w-4 h-4" />
                    View Application
                  </>
                ) : (
                  <>
                    Continue Application
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </div>

            {/* Application Steps */}
            <div className="mt-6 sm:mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              {(() => {
                const OTA_ENTRY = [
                  "Master of Occupational Therapy (OTA Entry)",
                  "Flex Master of Occupational Therapy (OTA Entry)",
                  "Hybrid Immersion Master of Occupational Therapy (OTA Entry)",
                ];
                const isOTAEntry = OTA_ENTRY.includes(activeApp.program_selection?.program_pathway);
                const RECO_PROGRAMS = ["Nursing", "Education", "Certificates"];
                const showReco = RECO_PROGRAMS.includes(activeApp.program_selection?.program_type);
                const dashSteps = [
                  { step: 1, label: "Personal", icon: User },
                  { step: 2, label: "Academic History", icon: GraduationCap },
                ];
                let n = 3;
                if (isOTAEntry) {
                  dashSteps.push({ step: n, label: "Employment History", icon: LayoutDashboard });
                  n++;
                }
                dashSteps.push({ step: n, label: "Documents Required", icon: Upload });
                n++;
                dashSteps.push({ step: n, label: "Official Documents", icon: Send });
                n++;
                if (showReco) {
                  dashSteps.push({ step: n, label: "Recommendation", icon: Star });
                  n++;
                }
                dashSteps.push({ step: n, label: "Review", icon: CheckCircle2 });
                return dashSteps;
              })().map(({ step, label, icon: Icon }) => (
                <div
                  key={step}
                  className={`p-4 rounded-xl border transition-colors cursor-pointer ${
                    step < activeApp.current_step
                      ? "bg-[#739600]/10 border-[#739600]/30"
                      : step === activeApp.current_step
                      ? "bg-[#00677F]/10 border-[#00677F]/50"
                      : theme === 'dark' ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"
                  }`}
                  onClick={() => navigate(`/application/${activeApp.id}/${step}`)}
                  data-testid={`step-${step}-nav`}
                >
                  <Icon className={`w-5 h-5 mb-2 ${
                    step < activeApp.current_step
                      ? "text-[#739600]"
                      : step === activeApp.current_step
                      ? "text-[#00677F]"
                      : theme === 'dark' ? "text-slate-500" : "text-gray-400"
                  }`} />
                  <p className={`text-sm font-medium ${
                    step <= activeApp.current_step 
                      ? (theme === 'dark' ? "text-white" : "text-gray-900") 
                      : (theme === 'dark' ? "text-slate-500" : "text-gray-500")
                  }`}>
                    {label}
                  </p>
                  {step < activeApp.current_step && (
                    <CheckCircle2 className="w-4 h-4 text-[#739600] mt-1" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submitted Applications */}
        {submittedApps.length > 0 && (
          <div className="mt-8">
            <h3 className={`font-['Montserrat'] text-xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Submitted Applications
            </h3>
            <div className="space-y-4">
              {submittedApps.map((app) => {
                // Get status label and color based on review_status
                const getStatusDisplay = (reviewStatus) => {
                  switch (reviewStatus) {
                    case "application_received":
                      return { label: "Application Received", color: "#00677F", bgColor: "rgba(0,180,216,0.2)" };
                    case "under_review":
                      return { label: "Under Review", color: "#FF9800", bgColor: "rgba(255,152,0,0.2)" };
                    case "decision":
                      return { label: "Decision", color: "#7B68EE", bgColor: "rgba(123,104,238,0.2)" };
                    case "admitted":
                      return { label: "Admitted", color: "#739600", bgColor: "rgba(40,167,69,0.2)" };
                    default:
                      return { label: "Application Received", color: "#00677F", bgColor: "rgba(0,180,216,0.2)" };
                  }
                };
                
                const statusDisplay = getStatusDisplay(app.review_status);
                
                return (
                  <div 
                    key={app.id}
                    className="glass-card rounded-2xl p-6"
                    data-testid={`submitted-app-${app.id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#739600]/20 flex items-center justify-center">
                          <CheckCircle2 className="w-6 h-6 text-[#739600]" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#739600]/20 text-[#739600]">
                              Submitted
                            </span>
                          </div>
                          <p className="text-white font-semibold text-lg">
                            {app.program_selection?.program_type || "Healthcare"}
                          </p>
                          <p className="text-slate-500 text-sm">
                            Submitted {new Date(app.submitted_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <span 
                        className="px-4 py-2 rounded-full text-sm font-medium self-start"
                        style={{ backgroundColor: statusDisplay.bgColor, color: statusDisplay.color }}
                        data-testid={`app-status-${app.id}`}
                      >
                        {statusDisplay.label}
                      </span>
                    </div>

                    {/* Program Details Grid - Same as Active Application */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                      {app.program_selection?.program_pathway && (
                        <div className="p-3 rounded-xl bg-white/5 border border-white/[0.05]">
                          <div className="flex items-center gap-2 mb-1">
                            <GraduationCap className="w-3.5 h-3.5 text-[#7B68EE]" />
                            <span className="text-slate-500 text-xs uppercase tracking-wider">Pathway</span>
                          </div>
                          <p className="text-white text-sm font-medium">{app.program_selection.program_pathway}</p>
                        </div>
                      )}
                      {(app.program_selection?.campus || app.program_selection?.primary_campus) && (
                        <div className="p-3 rounded-xl bg-white/5 border border-white/[0.05]">
                          <div className="flex items-center gap-2 mb-1">
                            <MapPin className="w-3.5 h-3.5 text-[#00677F]" />
                            <span className="text-slate-500 text-xs uppercase tracking-wider">Primary Campus</span>
                          </div>
                          <p className="text-white text-sm font-medium">{app.program_selection.campus || app.program_selection.primary_campus}</p>
                        </div>
                      )}
                      {app.program_selection?.start_term && (
                        <div className="p-3 rounded-xl bg-white/5 border border-white/[0.05]">
                          <div className="flex items-center gap-2 mb-1">
                            <Calendar className="w-3.5 h-3.5 text-[#FF9800]" />
                            <span className="text-slate-500 text-xs uppercase tracking-wider">Start Term</span>
                          </div>
                          <p className="text-white text-sm font-medium">{app.program_selection.start_term}</p>
                        </div>
                      )}
                      {app.program_selection?.secondary_campus && app.program_selection?.program_type === "Occupational Therapy" && (
                        <div className="p-3 rounded-xl bg-white/5 border border-white/[0.05]">
                          <div className="flex items-center gap-2 mb-1">
                            <MapPin className="w-3.5 h-3.5 text-[#739600]" />
                            <span className="text-slate-500 text-xs uppercase tracking-wider">Backup Campus</span>
                          </div>
                          <p className="text-white text-sm font-medium">{app.program_selection.secondary_campus}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Enrollment Advisor Card - At Bottom */}
        {advisor && (
          <div className="glass-card rounded-2xl p-6 mt-8" data-testid="enrollment-advisor-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>Your Enrollment Advisor</h3>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#739600]/20 text-[#739600]">
                Assigned
              </span>
            </div>
            
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              {/* Advisor Info */}
              <div className="flex items-center gap-4 flex-1">
                <img 
                  src={advisor.avatar_url} 
                  alt={advisor.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-[#00677F]/30"
                  data-testid="advisor-avatar"
                />
                <div>
                  <h4 className={`font-semibold text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-testid="advisor-name">
                    {advisor.name}
                  </h4>
                  <p className="text-[#00677F] text-sm">{advisor.title}</p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>
                    {displayApp?.program_selection?.program_type 
                      ? `${displayApp.program_selection.program_type} Programs` 
                      : advisor.specialization}
                  </p>
                </div>
              </div>
              
              {/* Contact Actions */}
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <button
                  onClick={() => setShowScheduleModal(true)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#00677F]/50 text-white' : 'bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-[#00677F]/50 text-gray-700'}`}
                  data-testid="schedule-appointment-btn"
                >
                  <CalendarDays className="w-4 h-4 text-[#00677F]" />
                  Schedule Appointment
                </button>
                
                <button
                  onClick={() => setShowEmailModal(true)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#7B68EE]/50 text-white' : 'bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-[#7B68EE]/50 text-gray-700'}`}
                  data-testid="email-advisor-btn"
                >
                  <Mail className="w-4 h-4 text-[#7B68EE]" />
                  Send Email
                </button>
                
                <button
                  onClick={() => setShowTextModal(true)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#739600]/50 text-white' : 'bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-[#739600]/50 text-gray-700'}`}
                  data-testid="text-advisor-btn"
                >
                  <MessageCircle className="w-4 h-4 text-[#739600]" />
                  Send Text
                </button>
                
                <button
                  onClick={() => setShowCallModal(true)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#FF6B35]/50 text-white' : 'bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-[#FF6B35]/50 text-gray-700'}`}
                  data-testid="call-advisor-btn"
                >
                  <Phone className="w-4 h-4 text-[#FF6B35]" />
                  Call
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && advisor && (
        <EmailModal 
          onClose={() => setShowEmailModal(false)} 
          advisor={advisor} 
          user={user} 
        />
      )}

      {/* Text Modal */}
      {showTextModal && advisor && (
        <TextModal 
          onClose={() => setShowTextModal(false)} 
          advisor={advisor} 
          user={user} 
        />
      )}

      {/* Call Modal */}
      {showCallModal && advisor && (
        <CallModal 
          onClose={() => setShowCallModal(false)} 
          advisor={advisor} 
          user={user} 
        />
      )}

      {/* Schedule Modal */}
      {showScheduleModal && advisor && (
        <ScheduleModal 
          onClose={() => setShowScheduleModal(false)} 
          advisor={advisor} 
          user={user} 
        />
      )}

      {/* New Application Modal */}
      {showNewAppModal && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto ${theme === 'dark' ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/40 backdrop-blur-md'}`}>
          <div className="glass-card rounded-3xl p-8 max-w-2xl w-full animate-slide-up my-8" data-testid="new-app-modal">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-['Montserrat'] text-2xl font-bold text-white">
                  {modalStep === 1 && (editingAppId ? "Change Your Program" : "Select Your Program")}
                  {modalStep === 2 && (editingAppId ? "Change Your Pathway" : "Select Your Pathway")}
                  {modalStep === 3 && (editingAppId ? "Update Campus & Term" : "Select Campus & Term")}
                </h2>
                <p className="text-slate-500 text-sm mt-1">Step {modalStep} of 3</p>
              </div>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="close-modal-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Progress Indicator */}
            <div className="flex gap-2 mb-8">
              {[1, 2, 3].map((step) => (
                <div 
                  key={step}
                  className={`h-1 flex-1 rounded-full transition-all ${
                    step <= modalStep ? "bg-[#00677F]" : "bg-white/10"
                  }`}
                />
              ))}
            </div>
            
            {/* Step 1: Program Category */}
            {modalStep === 1 && (
              <div className="space-y-4 mb-6">
                {Object.entries(PROGRAM_PATHWAYS).map(([key, category]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setSelectedCategory(key);
                      setSelectedProgram(null);
                    }}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      selectedCategory === key
                        ? "border-[#A1D8E0] bg-[#A1D8E0]/10"
                        : "border-white/10 bg-white/5 hover:border-white/30"
                    }`}
                    data-testid={`select-category-${key}`}
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ background: `${category.color}20` }}
                      >
                        {key === "ot" && <GraduationCap className="w-6 h-6" style={{ color: category.color }} />}
                        {key === "nursing" && <User className="w-6 h-6" style={{ color: category.color }} />}
                        {key === "education" && <BookOpen className="w-6 h-6" style={{ color: category.color }} />}
                        {key === "certificates" && <FileText className="w-6 h-6" style={{ color: category.color }} />}
                      </div>
                      <div>
                        <p className="text-white font-medium">{category.name}</p>
                        <p className="text-slate-500 text-sm">{category.programs.length} programs available</p>
                      </div>
                      {selectedCategory === key && (
                        <CheckCircle2 className="w-5 h-5 text-[#A1D8E0] ml-auto" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Step 2: Specific Program Pathway */}
            {modalStep === 2 && selectedCategory && (
              <div className="space-y-3 mb-6 max-h-[400px] overflow-y-auto pr-2">
                <p className="text-slate-400 text-sm mb-4">
                  Select your specific pathway within {PROGRAM_PATHWAYS[selectedCategory].name}:
                </p>
                {PROGRAM_PATHWAYS[selectedCategory].programs.map((program) => (
                  <button
                    key={program.id}
                    onClick={() => setSelectedProgram(program.id)}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      selectedProgram === program.id
                        ? "border-[#A1D8E0] bg-[#A1D8E0]/10"
                        : "border-white/10 bg-white/5 hover:border-white/30"
                    }`}
                    data-testid={`select-program-${program.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-white text-sm font-medium">{program.name}</p>
                      {selectedProgram === program.id && (
                        <CheckCircle2 className="w-5 h-5 text-[#A1D8E0]" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Step 3: Start Term & Campus */}
            {modalStep === 3 && (
              <div className="space-y-6 mb-6">
                {/* Selected Program Summary */}
                <div className="p-4 rounded-xl bg-[#00677F]/10 border border-[#00677F]/20">
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Selected Program</p>
                  <p className="text-white font-medium">
                    {PROGRAM_PATHWAYS[selectedCategory]?.programs.find(p => p.id === selectedProgram)?.name}
                  </p>
                </div>

                {/* Primary Campus Selection - Only for OT programs */}
                {isOTProgram(selectedCategory) ? (
                  <div className="space-y-3">
                    <Label className={`text-xs font-medium uppercase tracking-wider flex items-center gap-2 ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>
                      <MapPin className="w-4 h-4 text-[#00677F]" />
                      Primary Campus <span className="text-[#A1D8E0]">*</span>
                    </Label>
                    <Select value={selectedCampus || ""} onValueChange={setSelectedCampus}>
                      <SelectTrigger className={`h-12 rounded-xl px-4 ${theme === 'dark' ? 'bg-black/30 border-white/[0.08] text-white focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20' : 'bg-black/[0.03] border-black/[0.08] text-gray-900 focus:border-[#00677F] focus:ring-2 focus:ring-[#00677F]/20'}`} data-testid="select-campus">
                        <SelectValue placeholder="Select your preferred campus" />
                      </SelectTrigger>
                      <SelectContent className={`rounded-xl ${theme === 'dark' ? 'bg-[#11161F] border-white/10' : 'bg-white border-gray-200'}`}>
                        {CAMPUSES.filter(c => c.id !== "distance").map((campus) => (
                          <SelectItem key={campus.id} value={campus.id}>{campus.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Label className={`text-xs font-medium uppercase tracking-wider flex items-center gap-2 ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>
                      <MapPin className="w-4 h-4 text-[#00677F]" />
                      Campus
                    </Label>
                    <div className={`h-12 rounded-xl px-4 flex items-center ${theme === 'dark' ? 'bg-black/20 border border-white/[0.06] text-slate-300' : 'bg-gray-50 border border-gray-200 text-gray-700'}`} data-testid="campus-distance-display">
                      <MapPin className="w-4 h-4 text-[#00677F] mr-2" />
                      Distance
                    </div>
                  </div>
                )}

                {/* Start Term Selection */}
                <div className="space-y-3">
                  <Label className={`text-xs font-medium uppercase tracking-wider flex items-center gap-2 ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>
                    <Calendar className="w-4 h-4 text-[#FF9800]" />
                    Intended Start Term <span className="text-[#A1D8E0]">*</span>
                  </Label>
                  <Select value={selectedStartTerm || ""} onValueChange={setSelectedStartTerm}>
                    <SelectTrigger className={`h-12 rounded-xl px-4 ${theme === 'dark' ? 'bg-black/30 border-white/[0.08] text-white focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20' : 'bg-black/[0.03] border-black/[0.08] text-gray-900 focus:border-[#00677F] focus:ring-2 focus:ring-[#00677F]/20'}`} data-testid="select-start-term">
                      <SelectValue placeholder="Select your start term" />
                    </SelectTrigger>
                    <SelectContent className={`rounded-xl ${theme === 'dark' ? 'bg-[#11161F] border-white/10' : 'bg-white border-gray-200'}`}>
                      {START_TERMS.map((term) => (
                        <SelectItem key={term.id} value={term.id}>{term.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Secondary Campus Selection - Only for OT programs */}
                {isOTProgram(selectedCategory) && (
                  <div className="space-y-3">
                    <Label className={`text-xs font-medium uppercase tracking-wider flex items-center gap-2 ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>
                      <MapPin className="w-4 h-4 text-[#7B68EE]" />
                      Secondary Campus (Backup Option)
                    </Label>
                    <p className={`text-xs -mt-1 ${theme === 'dark' ? 'text-slate-600' : 'text-gray-500'}`}>In case your first choice reaches capacity</p>
                    <Select value={selectedCampus2 || ""} onValueChange={setSelectedCampus2}>
                      <SelectTrigger className={`h-12 rounded-xl px-4 ${theme === 'dark' ? 'bg-black/30 border-white/[0.08] text-white focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20' : 'bg-black/[0.03] border-black/[0.08] text-gray-900 focus:border-[#00677F] focus:ring-2 focus:ring-[#00677F]/20'}`} data-testid="select-campus-2">
                        <SelectValue placeholder="Select a backup campus (optional)" />
                      </SelectTrigger>
                      <SelectContent className={`rounded-xl ${theme === 'dark' ? 'bg-[#11161F] border-white/10' : 'bg-white border-gray-200'}`}>
                        {CAMPUSES.filter(c => c.id !== selectedCampus && c.id !== "distance").map((campus) => (
                          <SelectItem key={campus.id} value={campus.id}>{campus.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            
            {/* Navigation Buttons */}
            <div className="flex gap-3">
              {modalStep > 1 && (
                <Button
                  onClick={() => setModalStep(modalStep - 1)}
                  variant="outline"
                  className={`flex-1 rounded-xl h-12 ${theme === 'dark' ? 'border-white/20 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                >
                  Back
                </Button>
              )}
              
              {modalStep < 3 ? (
                <Button
                  onClick={() => setModalStep(modalStep + 1)}
                  disabled={
                    (modalStep === 1 && !selectedCategory) ||
                    (modalStep === 2 && !selectedProgram)
                  }
                  className="flex-1 bg-[#00677F] hover:bg-[#135163] text-white rounded-xl h-12 shadow-[0_0_20px_rgba(0,180,216,0.3)] disabled:opacity-50"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={editingAppId ? updateProgramSelection : createApplication}
                  disabled={!selectedStartTerm || (isOTProgram(selectedCategory) && !selectedCampus) || creatingApp}
                  className="flex-1 bg-[#739600] hover:bg-[#218838] text-white rounded-xl h-12 shadow-[0_0_20px_rgba(40,167,69,0.3)] disabled:opacity-50"
                  data-testid={editingAppId ? "update-program-btn" : "create-application-btn"}
                >
                  {creatingApp 
                    ? (editingAppId ? "Updating..." : "Creating...") 
                    : (editingAppId ? "Update Program" : "Create Application")
                  }
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Chat Modal */}
      {showChat && (
        <AIChat onClose={() => setShowChat(false)} />
      )}
    </div>
  );
};

export default Dashboard;
